/**
 * «موجودی کل کاربران» (owner 2026-10-17): Σ what all users hold, per asset — Toman and every coin — so
 * the same amounts can be sourced behind the scenes. Read-only server values (ledger truth); the
 * largest Toman value first, assets nobody holds hidden. The Toman value is an estimate from the
 * latest fresh rate («—» when none); amounts themselves are exact.
 */
import {
  AppContainer,
  Button,
  Card,
  cx,
  formatAmount,
  formatRelative,
  PageHeader,
} from '@hopex/ui';
import { PiggyBank, RefreshCw, Users } from 'lucide-react';
import { Remote, useRemote } from '../lib/remote.tsx';

interface Row {
  asset: string;
  nameFa: string;
  available: string;
  reserved: string;
  total: string;
  holders: number;
  tomanValue: string | null;
}

const unit = (asset: string) => (asset === 'IRT' ? 'TOMAN' : asset);
const isZero = (v: string) => /^0*(\.0*)?$/.test(v);

export function HoldingsPage() {
  const r = useRemote<{ asOf: string; totalToman: string | null; items: Row[] }>('/holdings');
  return (
    <AppContainer size="content">
      <PageHeader
        title="موجودی کل کاربران"
        description="جمع موجودی همه‌ی کاربران به تفکیک ارز؛ همین مقدار را باید پشت صحنه در اختیار داشته باشید. ارزهایی که کسی ندارد نمایش داده نمی‌شوند."
        actions={
          <Button
            variant="secondary"
            size="sm"
            onPress={r.reload}
            icon={<RefreshCw className="size-4" />}
          >
            تازه‌سازی
          </Button>
        }
      />
      <Remote r={r}>
        {(d) => {
          const max = d.items.reduce(
            (m, x) => (x.tomanValue && Number(x.tomanValue) > m ? Number(x.tomanValue) : m),
            0,
          );
          return (
            <div className="flex flex-col gap-4" data-holdings>
              <section
                data-theme-scope="dark"
                className="relative overflow-hidden rounded-[24px] border border-line-strong bg-canvas-deep p-5 text-fg sm:p-6"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_140%_at_100%_0%,color-mix(in_srgb,var(--hx-accent)_26%,transparent),transparent_62%)]"
                />
                <div className="relative flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <p className="m-0 flex items-center gap-2 text-[13px] text-fg-muted">
                      <PiggyBank aria-hidden className="size-4" /> ارزش تقریبی کل موجودی کاربران
                    </p>
                    <p
                      className="m-0 mt-2 text-[30px] font-black leading-none sm:text-[36px]"
                      data-holdings-total
                    >
                      <bdi dir="ltr" className="hx-num">
                        {d.totalToman ? formatAmount(d.totalToman, 'TOMAN') : '—'}
                      </bdi>
                    </p>
                  </div>
                  <p className="m-0 text-[12px] text-fg-muted">
                    {d.items.length.toLocaleString('fa-IR')} ارز · به‌روز {formatRelative(d.asOf)}
                  </p>
                </div>
              </section>
              {d.items.length === 0 ? (
                <Card className="p-8 text-center text-[14px] text-fg-muted">
                  هنوز کاربری موجودی ندارد.
                </Card>
              ) : (
                <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3">
                  {d.items.map((x, i) => {
                    const share =
                      x.tomanValue && max > 0 ? Math.max(4, (Number(x.tomanValue) / max) * 100) : 0;
                    return (
                      <li key={x.asset}>
                        <Card className="flex h-full flex-col gap-3 p-4" data-holding={x.asset}>
                          <div className="flex items-center gap-3">
                            <span
                              className={cx(
                                'grid size-11 shrink-0 place-items-center rounded-full text-[12px] font-black',
                                i === 0
                                  ? 'bg-accent text-on-accent'
                                  : 'bg-accent-tint text-accent-text',
                              )}
                            >
                              {x.asset === 'IRT' ? 'ت' : x.asset.slice(0, 4)}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="m-0 truncate text-[13px] text-fg-muted">{x.nameFa}</p>
                              <p className="m-0 truncate text-[19px] font-black">
                                <bdi dir="ltr" className="hx-num">
                                  {formatAmount(x.total, unit(x.asset))}
                                </bdi>
                              </p>
                            </div>
                          </div>
                          <div
                            className="h-1.5 overflow-hidden rounded-full bg-surface-2"
                            aria-hidden
                          >
                            <span
                              className="block h-full rounded-full bg-accent"
                              style={{ width: `${share}%` }}
                            />
                          </div>
                          <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12.5px]">
                            <dt className="text-fg-muted">آزاد</dt>
                            <dd className="m-0 text-end font-semibold">
                              <bdi dir="ltr" className="hx-num">
                                {formatAmount(x.available, unit(x.asset), { withUnit: false })}
                              </bdi>
                            </dd>
                            <dt className="text-fg-muted">رزرو برداشت</dt>
                            <dd className="m-0 text-end font-semibold">
                              <bdi dir="ltr" className="hx-num">
                                {isZero(x.reserved)
                                  ? '۰'
                                  : formatAmount(x.reserved, unit(x.asset), { withUnit: false })}
                              </bdi>
                            </dd>
                            <dt className="text-fg-muted">≈ ارزش تومانی</dt>
                            <dd className="m-0 text-end font-semibold">
                              <bdi dir="ltr" className="hx-num">
                                {x.tomanValue
                                  ? formatAmount(x.tomanValue, 'TOMAN', { withUnit: false })
                                  : '—'}
                              </bdi>
                            </dd>
                            <dt className="flex items-center gap-1 text-fg-muted">
                              <Users aria-hidden className="size-3.5" /> دارندگان
                            </dt>
                            <dd className="m-0 text-end font-semibold">
                              {x.holders.toLocaleString('fa-IR')} نفر
                            </dd>
                          </dl>
                        </Card>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        }}
      </Remote>
    </AppContainer>
  );
}
